import React, { useEffect, useRef } from 'react';
import {
  VirtualizedCombobox,
  VirtualizedComboboxOption,
} from '@roger/ui-components';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';

interface PayorComboboxProps {
  value?: string;
  onValueChange?: (value: string | null) => void;
  placeholder?: string;
  className?: string;
  buttonClassName?: string;
  isLoading?: boolean; // Add loading state prop
}

export const PayorCombobox: React.FC<PayorComboboxProps> = ({
  value,
  onValueChange,
  placeholder = 'Select a payor...',
  className,
  buttonClassName,
  isLoading = false, // Default to false
}) => {
  const { payors, payorsLoading, payorsError, fetchPayors } = useStore(
    useShallow(state => ({
      payors: state.payors,
      payorsLoading: state.payorsLoading,
      payorsError: state.payorsError,
      fetchPayors: state.fetchPayors,
    }))
  );
  const hasFetchedPayors = useRef(false);

  // Fetch payors on component mount if not already loaded
  useEffect(() => {
    if (!hasFetchedPayors.current && payors.length === 0 && !payorsLoading) {
      hasFetchedPayors.current = true;
      fetchPayors();
    }
  }, [payors.length, payorsLoading, fetchPayors]);

  // Convert payors to virtualized combobox options
  const options: VirtualizedComboboxOption[] = [
    {
      value: '',
      label: 'None',
    },
    ...payors.map(payor => ({
      value: payor.id,
      label: `${payor.payor_name}${
        payor.electronic_payor_id ? ` - ${payor.electronic_payor_id}` : ''
      }`,
    })),
  ];

  if (payorsLoading) {
    return (
      <VirtualizedCombobox
        options={[]}
        value={value || ''}
        onValueChange={onValueChange}
        placeholder='Loading payors...'
        className={className}
        buttonClassName={buttonClassName}
      />
    );
  }

  if (payorsError) {
    return (
      <VirtualizedCombobox
        options={[]}
        value={value || ''}
        onValueChange={onValueChange}
        placeholder='Error loading payors'
        className={className}
        buttonClassName={buttonClassName}
      />
    );
  }

  return (
    <VirtualizedCombobox
      options={options}
      value={value || ''}
      onValueChange={onValueChange}
      placeholder={placeholder}
      searchPlaceholder='Search payors...'
      className={className}
      buttonClassName={buttonClassName}
      height='300px'
      popoverWidth='500px'
      isLoading={isLoading}
    />
  );
};
